<template>
  <div class="undo-bar py-3 px-4 caption">
    <!-- Duplicate variant: names both sides of the merge. Each title truncates
         inside its own box so a long first title can never swallow the second —
         the half that answers "where did my reference go?". -->
    <div v-if="isDuplicate" class="merge-summary mr-3">
      <v-icon class="material-icons-outlined" small>
        content_copy
      </v-icon>
      <span class="merge-label">Merged</span>
      <span class="merge-title font-weight-bold">
        {{ publication.title }}
      </span>
      <span class="merge-label">into</span>
      <span class="merge-title font-weight-bold">
        {{ mergedIntoTitle }}
      </span>
    </div>

    <!-- The publication title -->
    <div v-else class="text-overflow-ellipsis mr-3">
      {{ publication.title }}
    </div>

    <div class="actions">
      <!-- Visualisation of the vote by the user -->
      <div
        v-if="!isDuplicate"
        class="text d-flex flex-nowrap mr-2"
        :class="textColor"
      >
        <v-icon
          :color="color"
          class="material-icons-outlined mr-1"
          small
        >
          {{ icon }}
        </v-icon>
        {{ text }}
      </div>

      <!-- Undo text button -->
      <v-btn color="primary" text small @click="$emit('undo')">
        <v-icon left>undo</v-icon>
        <span class="text-none">Undo</span>
      </v-btn>

      <!-- Jump to the Duplicates view -->
      <v-btn
        v-if="isDuplicate"
        color="primary"
        text
        small
        @click="$emit('view-duplicates')"
      >
        <span class="text-none">View in Duplicates</span>
      </v-btn>

      <!-- Dismiss the merge bar. Only the duplicate variant needs this: the
           vote variant is replaced by the next vote, whereas a merge bar with
           no dismiss would sit there suppressing the vote bar. -->
      <v-btn
        v-if="isDuplicate"
        icon
        small
        aria-label="Dismiss"
        @click="$emit('dismiss')"
      >
        <v-icon small>close</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
import { Publication } from '@/models';
import { RelevanceAssessment, InclusionAssessment } from '@/enums';

const VOTE_TO_PROPS = {
  [RelevanceAssessment.RELEVANT]: {
    text: 'Relevant',
    color: 'success',
    textColor: 'success--text',
    icon: 'thumb_up',
  },
  [RelevanceAssessment.NOT_SURE]: {
    text: 'Not Sure',
    color: 'warning',
    textColor: 'warning--text',
    icon: 'help',
  },
  [RelevanceAssessment.NOT_RELEVANT]: {
    text: 'Not Relevant',
    color: 'error',
    textColor: 'error--text',
    icon: 'thumb_down',
  },
  [InclusionAssessment.INCLUDE]: {
    text: 'Included',
    color: 'green',
    icon: 'thumb_up',
  },
  [InclusionAssessment.EXCLUDE]: {
    text: 'Excluded',
    color: 'error',
    icon: 'thumb_down',
  },
};

export default {
  name: 'UndoBar',
  props: {
    publication: {
      type: [Object, Publication],
      required: true,
    },
    vote: {
      type: String,
      default: null,
    },
    mergedIntoTitle: {
      type: String,
      default: null,
    },
  },
  emits: ['undo', 'view-duplicates', 'dismiss'],
  computed: {
    isDuplicate() {
      return !!this.mergedIntoTitle;
    },
    icon() {
      return this.voteProps.icon;
    },
    text() {
      return this.voteProps.text;
    },
    textColor() {
      return this.voteProps.textColor;
    },
    color() {
      return this.voteProps.color;
    },
    voteProps() {
      return VOTE_TO_PROPS[this.vote] ?? {};
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/styles.sass';

.undo-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;

  border-top: 1px solid map-get($grey, 'lighten-2');
  color: map-get($grey, 'darken-1');
  background-color: white;

  .actions {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;

    .text {
      white-space: nowrap;
    }
  }

  max-height: 53px;

  .text-overflow-ellipsis {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .merge-summary {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 4px;
    min-width: 0;
    overflow: hidden;

    .merge-label {
      white-space: nowrap;
      flex: 0 0 auto;
    }

    // Two truncation points, one per title, each capped so neither title can
    // consume the whole line and hide the other.
    .merge-title {
      flex: 0 1 auto;
      min-width: 0;
      max-width: 50%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}
</style>
